import React, { useState, useEffect, useRef } from 'react';
import './DriverDataView.css';
import { fetchPrimaryLoadingLocations, searchSecondaryLoadingLocations, type LoadingLocation } from '../api/loadingLocations';

interface DriverDataViewProps {
  batchTransportId: string;
  lifnr?: string; // LIFNR des Lieferanten für primäre Ladeorte
  initialData: {
    driverName?: string;
    truckLicensePlate?: string;
    loadingLocationName?: string;
    supplierReferenceNumber?: string;
  };
  onClose: () => void;
  onChange: (data: {
    driverName?: string | null;
    truckLicensePlate?: string | null;
    loadingLocationName?: string | null;
    supplierReferenceNumber?: string | null;
  }) => void;
}

export const DriverDataView: React.FC<DriverDataViewProps> = ({
  batchTransportId,
  lifnr,
  initialData,
  onClose,
  onChange
}) => {
  const [driverName, setDriverName] = useState(initialData.driverName || '');
  const [truckLicensePlate, setTruckLicensePlate] = useState(initialData.truckLicensePlate || '');
  const [loadingLocationName, setLoadingLocationName] = useState(initialData.loadingLocationName || '');
  const [supplierReferenceNumber, setSupplierReferenceNumber] = useState(initialData.supplierReferenceNumber || '');
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Loading Locations ValueHelp State
  const [primaryLocations, setPrimaryLocations] = useState<LoadingLocation[]>([]);
  const [secondaryLocations, setSecondaryLocations] = useState<LoadingLocation[]>([]);
  const [loadingPrimary, setLoadingPrimary] = useState(false);
  const [loadingSecondary, setLoadingSecondary] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Track the current batch transport ID to detect changes
  const previousBatchTransportIdRef = useRef(batchTransportId);

  // Update local state only when switching to a different batch transport
  useEffect(() => {
    if (previousBatchTransportIdRef.current !== batchTransportId) {
      setDriverName(initialData.driverName || '');
      setTruckLicensePlate(initialData.truckLicensePlate || '');
      setLoadingLocationName(initialData.loadingLocationName || '');
      setSupplierReferenceNumber(initialData.supplierReferenceNumber || '');

      // Reset search and dropdown state when switching batch transports
      setSearchTerm('');
      setShowDropdown(false);
      setPrimaryLocations([]);
      setSecondaryLocations([]);

      previousBatchTransportIdRef.current = batchTransportId;
    }
  }, [batchTransportId, initialData]);

  // Load primary loading locations when LIFNR is available
  useEffect(() => {
    if (!lifnr) {
      setPrimaryLocations([]);
      return;
    }

    const loadPrimaryLocations = async () => {
      setLoadingPrimary(true);
      try {
        const locations = await fetchPrimaryLoadingLocations(lifnr);
        setPrimaryLocations(locations);

        // Auto-select if exactly 1 location returned
        if (locations.length === 1) {
          const location = locations[0];
          setLoadingLocationName(location.displayText);
          // Use debouncedSave to ensure consistent filtering
          debouncedSave({ loadingLocationName: location.displayText });
        }
      } catch (error) {
        console.error("Failed to load primary loading locations:", error);
        setPrimaryLocations([]);
      } finally {
        setLoadingPrimary(false);
      }
    };

    void loadPrimaryLocations();
  }, [lifnr]);

  // Search secondary locations when user types
  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2) {
      setSecondaryLocations([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoadingSecondary(true);
      try {
        const locations = await searchSecondaryLoadingLocations(searchTerm, 10);
        setSecondaryLocations(locations);
      } catch (error) {
        console.error("Failed to search loading locations:", error);
        setSecondaryLocations([]);
      } finally {
        setLoadingSecondary(false);
      }
    }, 300); // Debounce 300ms

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleDriverNameChange = (value: string) => {
    // Normalize driver name: uppercase
    const normalized = value.toUpperCase();
    setDriverName(normalized);
    debouncedSave({ driverName: normalized });
  };

  const handleTruckLicensePlateChange = (value: string) => {
    // Normalize license plate: uppercase, no hyphens, no spaces
    const normalized = value.toUpperCase().replace(/[-\s]/g, '');
    setTruckLicensePlate(normalized);
    debouncedSave({ truckLicensePlate: normalized });
  };

  const handleLoadingLocationChange = (value: string) => {
    setLoadingLocationName(value);
    setSearchTerm(value); // Trigger secondary search
    debouncedSave({ loadingLocationName: value });
  };

  const handleLoadingLocationFocus = () => {
    setShowDropdown(true);

    // If no LIFNR (no contract), immediately load all secondary locations
    if (!lifnr && secondaryLocations.length === 0) {
      const loadAllLocations = async () => {
        setLoadingSecondary(true);
        try {
          // Search with empty string to get all locations (limited to 50)
          const locations = await searchSecondaryLoadingLocations('', 50);
          setSecondaryLocations(locations);
        } catch (error) {
          console.error("Failed to load all loading locations:", error);
        } finally {
          setLoadingSecondary(false);
        }
      };
      void loadAllLocations();
    }

    setSearchTerm(loadingLocationName); // Initialize search with current value
  };

  const handleSelectLoadingLocation = (location: LoadingLocation) => {
    setLoadingLocationName(location.displayText);
    setSearchTerm('');
    setShowDropdown(false);
    debouncedSave({ loadingLocationName: location.displayText });
  };

  const handleSupplierReferenceChange = (value: string) => {
    setSupplierReferenceNumber(value);
    debouncedSave({ supplierReferenceNumber: value });
  };

  // Debounced save function (1 second delay)
  const debouncedSave = (data: {
    driverName?: string;
    truckLicensePlate?: string;
    loadingLocationName?: string;
    supplierReferenceNumber?: string;
  }) => {
    // Clear existing timer
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    // Set new timer
    saveTimerRef.current = setTimeout(async () => {
      try {
        // Convert empty strings to null, send non-empty strings as-is
        const payload: Record<string, string | null> = {};

        if (data.driverName !== undefined) {
          payload.driverName = data.driverName === '' ? null : data.driverName;
        }
        if (data.truckLicensePlate !== undefined) {
          payload.truckLicensePlate = data.truckLicensePlate === '' ? null : data.truckLicensePlate;
        }
        if (data.loadingLocationName !== undefined) {
          payload.loadingLocationName = data.loadingLocationName === '' ? null : data.loadingLocationName;
        }
        if (data.supplierReferenceNumber !== undefined) {
          payload.supplierReferenceNumber = data.supplierReferenceNumber === '' ? null : data.supplierReferenceNumber;
        }

        // Only save if there are fields to update
        if (Object.keys(payload).length > 0) {
          await onChange(payload);
        }

        setSaveMessage("✓ Gespeichert");
        setTimeout(() => setSaveMessage(null), 2000);
      } catch (error) {
        console.error("Save error:", error);
        setSaveMessage("✗ Fehler beim Speichern");
        setTimeout(() => setSaveMessage(null), 3000);
      }
    }, 1000);
  };

  return (
    <div className="driver-data-view">
      {/* Save Notification */}
      {saveMessage && (
        <div className={`driver-data-save-notification ${saveMessage.includes('✓') ? 'success' : 'error'}`}>
          {saveMessage}
        </div>
      )}

      <div className="driver-data-view-content">
        {/* Fahrername */}
        <div className="driver-data-field-box">
          <label className="driver-data-field-label">
            Fahrername
          </label>
          <input
            type="text"
            className="driver-data-field-input"
            value={driverName}
            onChange={(e) => handleDriverNameChange(e.target.value)}
            placeholder="Name des Fahrers eingeben..."
          />
        </div>

        {/* LKW-Kennzeichen */}
        <div className="driver-data-field-box">
          <label className="driver-data-field-label">
            LKW-Kennzeichen
          </label>
          <input
            type="text"
            className="driver-data-field-input"
            value={truckLicensePlate}
            onChange={(e) => handleTruckLicensePlateChange(e.target.value)}
            placeholder="z.B. SOKTR1234"
          />
        </div>

        {/* Ladeort */}
        <div className="driver-data-field-box" style={{ position: 'relative' }}>
          <label className="driver-data-field-label">
            Ladeort
          </label>
          <input
            ref={inputRef}
            type="text"
            className="driver-data-field-input"
            value={loadingLocationName}
            onChange={(e) => handleLoadingLocationChange(e.target.value)}
            onFocus={handleLoadingLocationFocus}
            placeholder="Ladeort wählen oder eingeben..."
          />

          {/* Dropdown mit ValueHelp */}
          {showDropdown && (
            <div ref={dropdownRef} className="driver-data-dropdown">
              {loadingPrimary || loadingSecondary ? (
                <div className="driver-data-dropdown-loading">Lädt...</div>
              ) : (
                <>
                  {/* Primäre Ladeorte (aus ZHB_T24) */}
                  {primaryLocations.filter(location =>
                    !searchTerm ||
                    searchTerm.length < 2 ||
                    location.displayText.toLowerCase().includes(searchTerm.toLowerCase())
                  ).length > 0 && (
                      <>
                        <div className="driver-data-dropdown-section-header">
                          Zugeordnete Ladeorte
                        </div>
                        {primaryLocations.filter(location =>
                          !searchTerm ||
                          searchTerm.length < 2 ||
                          location.displayText.toLowerCase().includes(searchTerm.toLowerCase())
                        ).map((location) => (
                          <div
                            key={location.LADEORTNR}
                            className="driver-data-dropdown-item"
                            onClick={() => handleSelectLoadingLocation(location)}
                          >
                            <div className="driver-data-dropdown-item-text">
                              {location.displayText}
                            </div>
                          </div>
                        ))}
                      </>
                    )}

                  {/* Sekundäre Ladeorte (aus ZHB_T02 Suche) */}
                  {secondaryLocations.length > 0 && (
                    <>
                      {primaryLocations.filter(location =>
                        !searchTerm ||
                        searchTerm.length < 2 ||
                        location.displayText.toLowerCase().includes(searchTerm.toLowerCase())
                      ).length > 0 && (
                          <div className="driver-data-dropdown-divider"></div>
                        )}
                      <div className="driver-data-dropdown-section-header">
                        Weitere Ladeorte
                      </div>
                      {secondaryLocations.map((location) => (
                        <div
                          key={location.LADEORTNR}
                          className="driver-data-dropdown-item"
                          onClick={() => handleSelectLoadingLocation(location)}
                        >
                          <div className="driver-data-dropdown-item-text">
                            {location.displayText}
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  {/* Keine Ergebnisse */}
                  {primaryLocations.filter(location =>
                    !searchTerm ||
                    searchTerm.length < 2 ||
                    location.displayText.toLowerCase().includes(searchTerm.toLowerCase())
                  ).length === 0 && secondaryLocations.length === 0 && (
                      <div className="driver-data-dropdown-empty">
                        {searchTerm.length < 2
                          ? "Mindestens 2 Zeichen eingeben..."
                          : "Keine Ladeorte gefunden"}
                      </div>
                    )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Lieferanten-Beleg-Nr. */}
        <div className="driver-data-field-box">
          <label className="driver-data-field-label">
            Lieferanten-Beleg-Nr.
          </label>
          <input
            type="text"
            className="driver-data-field-input"
            value={supplierReferenceNumber}
            onChange={(e) => handleSupplierReferenceChange(e.target.value)}
            placeholder="Referenznummer eingeben..."
          />
        </div>
      </div>
    </div>
  );
};
